import type { FileReference } from '../../../api/types';
import { Icon } from '../Icon';
import styles from './Citations.module.css';

export interface CitationsProps {
  files?: FileReference[];
  className?: string;
}

/**
 * Displays file references from assistant messages as downloadable cards
 * Shows file name, size, and download link for source documents
 */
export function Citations({ files, className = '' }: CitationsProps) {
  if (!files || files.length === 0) {
    return null;
  }

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getFileExtension = (fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    return ext ? ext.toUpperCase() : 'FILE';
  };

  const getFileTypeColor = (fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return '#e74c3c'; // Red for PDF
      case 'doc':
      case 'docx':
        return '#2b5797'; // Blue for Word
      case 'xls':
      case 'xlsx':
        return '#217346'; // Green for Excel
      case 'ppt':
      case 'pptx':
        return '#d24726'; // Orange for PowerPoint
      case 'txt':
        return '#607d8b'; // Gray for text
      default:
        return '#0078d4'; // Default blue
    }
  };

  return (
    <div className={`${styles.citations} ${className}`}>
      <div className={styles.citationsHeader}>
        <span className={styles.headerText}>References:</span>
      </div>
      <div className={styles.fileGrid}>
        {files.map((file) => (
          <a
            key={file.id || file.file_id}
            href={file.file_metadata?.sharepoint_file_url}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.fileCard}
            title={`Download ${file.file_name}`}
          >
            <div 
              className={styles.fileIcon}
              style={{ backgroundColor: getFileTypeColor(file.file_name) }}
            >
              <span className={styles.fileExtension}>{getFileExtension(file.file_name)}</span>
            </div>
            <div className={styles.fileInfo}>
              <div className={styles.fileName}>{file.file_name}</div>
              {file.file_size && (
                <div className={styles.fileSize}>{formatFileSize(file.file_size)}</div>
              )}
            </div>
            <div className={styles.downloadIcon}>
              <Icon name="open-in-new" size={16} />
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}